import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { campaigns, subscribers as subscribersApi, type Campaign, type AutoReplyLogEntry } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import {
  ArrowLeft, Save, Send, Eye, EyeOff, Loader2, Check, Copy,
  Type, FileText, Mail, Clock, Sparkles, Code, AlignLeft,
  Users, Smartphone, Monitor, MessageSquareReply, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle2, Pause, Play,
} from "lucide-react";

const inp = "w-full px-4 py-2.5 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50 transition-all";
const btnPrimary = "px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-50 transition-colors shadow-sm shadow-coral/20";
const btnSecondary = "px-5 py-2.5 rounded-full border border-black/[0.08] text-ink text-sm font-semibold hover:border-black/20 disabled:opacity-50 transition-colors";

function Toast({ msg, type }: { msg: string; type: "ok" | "err" }) {
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-2xl shadow-xl text-sm font-semibold max-w-sm
      ${type === "ok" ? "bg-ink text-white" : "bg-red-500 text-white"}`}>
      {type === "ok" ? <Check size={14} /> : <span>✗</span>}
      <span>{msg}</span>
    </div>
  );
}

export default function CampaignEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "ok" | "err" } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showHtml, setShowHtml] = useState(false);
  const [copied, setCopied] = useState(false);
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const htmlTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [plainTextContent, setPlainTextContent] = useState("");

  // Editable fields
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyInstructions, setAutoReplyInstructions] = useState("");
  const [autoReplyLog, setAutoReplyLog] = useState<AutoReplyLogEntry[]>([]);
  const [autoReplyLogLoading, setAutoReplyLogLoading] = useState(false);
  const [expandedReplyId, setExpandedReplyId] = useState<string | null>(null);

  // Personalized preview
  const [showPersonalized, setShowPersonalized] = useState(false);
  const [personalizedLoading, setPersonalizedLoading] = useState(false);
  const [personalizedData, setPersonalizedData] = useState<{
    subscriber: { id: string; email: string; name: string | null };
    subject: string;
    html: string;
    preview_text: string;
  } | null>(null);
  const [subList, setSubList] = useState<Array<{ id: string; email: string; name: string | null }>>([]);
  const [selectedSubId, setSelectedSubId] = useState<string>("");
  const [personalizedView, setPersonalizedView] = useState<"desktop" | "mobile">("desktop");

  const showToast = (msg: string, type: "ok" | "err") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { campaign: c } = await campaigns.get(id);
      setCampaign(c);
      setName(c.name || "");
      setSubject(c.subject || "");
      setPreviewText(c.preview_text || "");
      setHtmlContent(c.html_content || "");
      setAutoReplyEnabled(!!(c.auto_reply_enabled));
      setAutoReplyInstructions(c.auto_reply_instructions || "");
    } catch (err: any) {
      showToast(err.message || "Failed to load campaign", "err");
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Helper: convert HTML to plain text for the rich text editor
  const htmlToPlainText = (html: string) =>
    html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  // Helper: convert plain text to HTML
  const plainTextToHtml = (text: string) =>
    text.split("\n\n").map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");

  // Sync plainTextContent whenever htmlContent changes externally (load, etc.)
  // but NOT when the user is actively typing in rich text mode
  const richTextTypingRef = useRef(false);
  useEffect(() => {
    if (!richTextTypingRef.current) {
      setPlainTextContent(htmlToPlainText(htmlContent));
    }
  }, [htmlContent]);

  const loadAutoReplyLog = useCallback(async () => {
    if (!id) return;
    setAutoReplyLogLoading(true);
    try {
      const res = await campaigns.autoReplyLog(id);
      setAutoReplyLog(res.log);
    } catch { /* ignore */ }
    setAutoReplyLogLoading(false);
  }, [id]);

  // Load reply log when auto-reply is enabled and campaign is loaded
  useEffect(() => {
    if (campaign?.auto_reply_enabled) loadAutoReplyLog();
  }, [campaign?.auto_reply_enabled, loadAutoReplyLog]);

  const save = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const { campaign: updated } = await campaigns.update(id, {
        name,
        subject: subject || undefined,
        preview_text: previewText || undefined,
        html_content: htmlContent || undefined,
        auto_reply_enabled: autoReplyEnabled ? 1 : 0,
        auto_reply_instructions: autoReplyInstructions || null,
      });
      setCampaign(updated);
      showToast("Campaign saved", "ok");
    } catch (err: any) {
      showToast(err.message || "Failed to save", "err");
    }
    setSaving(false);
  };

  // Load subscriber list for the picker
  const loadSubList = useCallback(async () => {
    try {
      const res = await subscribersApi.list({ maxResults: 50, status: "active" });
      setSubList(res.subscribers.map((s: any) => ({ id: s.id, email: s.email, name: s.name })));
    } catch { /* ignore */ }
  }, []);

  // Load personalized preview
  const loadPersonalized = async (subId?: string) => {
    if (!id) return;
    setPersonalizedLoading(true);
    try {
      const data = await campaigns.previewPersonalized(id, subId || undefined);
      setPersonalizedData(data);
      if (data.subscriber && !subId) {
        setSelectedSubId(data.subscriber.id);
      }
    } catch (err: any) {
      showToast(err.message || "Failed to load preview", "err");
    }
    setPersonalizedLoading(false);
  };

  // Open personalized preview
  const openPersonalized = () => {
    setShowPersonalized(true);
    setShowPreview(false);
    loadSubList();
    loadPersonalized();
  };

  const handleSend = async () => {
    if (!id || !confirm("Send this campaign to all subscribers?")) return;
    try {
      await campaigns.send(id);
      showToast("Campaign is sending!", "ok");
      load();
    } catch (err: any) {
      showToast(err.message || "Failed to send", "err");
    }
  };

  const handlePause = async () => {
    if (!id || !confirm("Pause this campaign? Unsent emails will be held.")) return;
    try {
      await campaigns.pause(id);
      showToast("Campaign paused", "ok");
      load();
    } catch (err: any) {
      showToast(err.message || "Failed to pause", "err");
    }
  };

  const handleResume = async () => {
    if (!id) return;
    try {
      await campaigns.resume(id);
      showToast("Campaign resumed", "ok");
      load();
    } catch (err: any) {
      showToast(err.message || "Failed to resume", "err");
    }
  };

  const copyId = () => {
    if (id) {
      navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading campaign…
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate">Campaign not found</p>
        <button onClick={() => navigate("/campaigns")} className={`${btnSecondary} mt-4`}>
          ← Back to campaigns
        </button>
      </div>
    );
  }

  const isDirty = name !== (campaign.name || "") ||
    subject !== (campaign.subject || "") ||
    previewText !== (campaign.preview_text || "") ||
    htmlContent !== (campaign.html_content || "") ||
    autoReplyEnabled !== !!(campaign.auto_reply_enabled) ||
    autoReplyInstructions !== (campaign.auto_reply_instructions || "");

  const statusColor: Record<string, string> = {
    draft: "bg-black/[0.06] text-slate",
    scheduled: "bg-blue-50 text-blue-600",
    sending: "bg-amber-50 text-amber-600",
    sent: "bg-green-50 text-green-600",
    paused: "bg-red-50 text-red-600",
  };

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate("/campaigns")} className="p-2 rounded-lg text-slate hover:text-ink hover:bg-black/[0.04]">
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-ink truncate">{campaign.name}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusColor[campaign.status] || statusColor.draft}`}>
                  {campaign.status}
                </span>
                <button onClick={copyId} className="text-[10px] font-mono text-slate/40 hover:text-coral transition-colors" title="Copy campaign ID">
                  {copied ? "Copied!" : id}
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setShowPreview(!showPreview); setShowPersonalized(false); }} className={btnSecondary}>
              {showPreview ? <><EyeOff size={14} className="inline mr-1.5" />Editor</> : <><Eye size={14} className="inline mr-1.5" />Preview</>}
            </button>
            <button onClick={() => { if (showPersonalized) { setShowPersonalized(false); } else { openPersonalized(); } }} className={`${btnSecondary} ${showPersonalized ? "!border-coral !text-coral" : ""}`}>
              <Users size={14} className="inline mr-1.5" />{showPersonalized ? "Close" : "Live Preview"}
            </button>
            <button onClick={save} disabled={saving || !isDirty} className={btnPrimary}>
              <Save size={14} className="inline mr-1.5" />
              {saving ? "Saving…" : "Save"}
            </button>
            {campaign.status === "draft" && (
              <button onClick={handleSend} className="px-5 py-2.5 rounded-full bg-teal text-white text-sm font-semibold hover:bg-teal/90 transition-colors shadow-sm">
                <Send size={14} className="inline mr-1.5" />Send
              </button>
            )}
            {(campaign.status === "sending" || campaign.status === "scheduled") && (
              <button onClick={handlePause} className="px-5 py-2.5 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors shadow-sm">
                <Pause size={14} className="inline mr-1.5" />Pause
              </button>
            )}
            {campaign.status === "paused" && (
              <button onClick={handleResume} className="px-5 py-2.5 rounded-full bg-teal text-white text-sm font-semibold hover:bg-teal/90 transition-colors shadow-sm">
                <Play size={14} className="inline mr-1.5" />Resume
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-5">

        {/* Campaign ID */}
        <div className="flex items-center gap-2 p-3 rounded-xl bg-black/[0.02] border border-black/[0.06]">
          <Copy size={13} className="text-slate/40 shrink-0" />
          <span className="text-xs text-slate">Campaign ID:</span>
          <code className="text-xs font-mono text-ink select-all">{id}</code>
          <button onClick={copyId} className="text-xs text-coral hover:underline ml-auto">
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>

        {showPersonalized ? (
          /* ─── Personalized Preview ─── */
          <div className="space-y-4">
            {/* Subscriber picker */}
            <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate mb-3">
                <Users size={13} /> Preview as subscriber
              </label>
              <div className="flex items-center gap-3">
                <select
                  value={selectedSubId}
                  onChange={(e) => {
                    setSelectedSubId(e.target.value);
                    loadPersonalized(e.target.value);
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50 transition-all"
                >
                  {subList.length === 0 && personalizedData?.subscriber && (
                    <option value={personalizedData.subscriber.id}>
                      {personalizedData.subscriber.email}{personalizedData.subscriber.name ? ` — ${personalizedData.subscriber.name}` : ""}
                    </option>
                  )}
                  {subList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.email}{s.name ? ` — ${s.name}` : ""}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => loadPersonalized(selectedSubId)}
                  disabled={personalizedLoading}
                  className={btnSecondary}
                >
                  {personalizedLoading ? <Loader2 size={14} className="animate-spin" /> : "Refresh"}
                </button>
              </div>
            </div>

            {personalizedLoading && !personalizedData ? (
              <div className="flex items-center justify-center h-48 text-slate">
                <Loader2 size={20} className="animate-spin mr-2" /> Generating preview…
              </div>
            ) : personalizedData ? (
              <>
                {/* Resolved subject & meta */}
                <div className="bg-white rounded-2xl border border-black/[0.06] p-5 space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate w-16 shrink-0">To</span>
                    <span className="text-sm text-ink">
                      {personalizedData.subscriber.name ? `${personalizedData.subscriber.name} <${personalizedData.subscriber.email}>` : personalizedData.subscriber.email}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate w-16 shrink-0">Subject</span>
                    <span className="text-sm font-semibold text-ink">{personalizedData.subject || "(empty)"}</span>
                  </div>
                  {personalizedData.preview_text && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate w-16 shrink-0">Preview</span>
                      <span className="text-xs text-slate">{personalizedData.preview_text}</span>
                    </div>
                  )}
                </div>

                {/* Desktop / Mobile toggle */}
                <div className="flex gap-1 bg-black/[0.03] rounded-xl p-1 w-fit">
                  <button
                    onClick={() => setPersonalizedView("desktop")}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                      personalizedView === "desktop" ? "bg-white shadow-sm text-ink" : "text-slate hover:text-ink"
                    }`}
                  >
                    <Monitor size={13} /> Desktop
                  </button>
                  <button
                    onClick={() => setPersonalizedView("mobile")}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                      personalizedView === "mobile" ? "bg-white shadow-sm text-ink" : "text-slate hover:text-ink"
                    }`}
                  >
                    <Smartphone size={13} /> Mobile
                  </button>
                </div>

                {/* Rendered email */}
                <div className={`bg-white rounded-2xl border border-black/[0.06] overflow-hidden ${personalizedView === "mobile" ? "max-w-[375px] mx-auto" : ""}`}>
                  <div className="px-5 py-3 border-b border-black/[0.06] bg-black/[0.01] flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-coral/10 flex items-center justify-center text-coral text-xs font-bold">
                      {(personalizedData.subscriber.name || personalizedData.subscriber.email)[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">{personalizedData.subject}</p>
                      <p className="text-[11px] text-slate truncate">
                        to {personalizedData.subscriber.email}
                      </p>
                    </div>
                  </div>
                  <iframe
                    srcDoc={personalizedData.html}
                    className="w-full border-none bg-white"
                    style={{ height: personalizedView === "mobile" ? "480px" : "560px" }}
                    sandbox="allow-same-origin"
                    title="Personalized email preview"
                  />
                </div>
              </>
            ) : (
              <div className="text-center text-slate text-sm py-12">
                No subscribers available for preview. Push leads from AutoResearch first.
              </div>
            )}
          </div>
        ) : showPreview ? (
          /* ─── Preview ─── */
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
            <div className="px-5 py-4 border-b border-black/[0.06] bg-black/[0.01]">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate font-semibold">Subject:</span>
                <span className="text-ink">{subject || "(no subject)"}</span>
              </div>
              {previewText && (
                <div className="flex items-center gap-2 text-xs mt-1">
                  <span className="text-slate">Preview:</span>
                  <span className="text-slate/70">{previewText}</span>
                </div>
              )}
            </div>
            <div className="p-6">
              {htmlContent ? (
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlContent) }} className="prose prose-sm max-w-none" />
              ) : (
                <p className="text-slate italic text-sm">No content yet. Switch to Editor to add content.</p>
              )}
            </div>
          </div>
        ) : (
          /* ─── Editor ─── */
          <>
            {/* Name */}
            <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate mb-2">
                <Type size={13} /> Campaign Name
              </label>
              <input value={name} onChange={(e) => setName(e.target.value)} className={inp} placeholder="My Campaign" />
            </div>

            {/* Subject & Preview Text */}
            <div className="bg-white rounded-2xl border border-black/[0.06] p-5 space-y-4">
              <div>
                <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate mb-2">
                  <Mail size={13} /> Subject Line
                </label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} className={inp}
                  placeholder="e.g. {{meta.personalized_subject}}" />
                <p className="text-[11px] text-slate/50 mt-1.5">
                  Use <code className="bg-black/[0.04] px-1 rounded">{'{{meta.personalized_subject}}'}</code> for dynamic subjects from AutoResearch
                </p>
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate mb-2">
                  <FileText size={13} /> Preview Text
                </label>
                <input value={previewText} onChange={(e) => setPreviewText(e.target.value)} className={inp}
                  placeholder="Text shown in inbox preview…" />
              </div>
            </div>

            {/* HTML Content */}
            <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-black/[0.06]">
                <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate">
                  {showHtml ? <Code size={13} /> : <AlignLeft size={13} />}
                  Email Body
                </label>
                <button onClick={() => setShowHtml(!showHtml)}
                  className="text-xs text-slate hover:text-ink font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-black/[0.04] transition-colors">
                  {showHtml ? <><AlignLeft size={12} /> Rich Text</> : <><Code size={12} /> HTML</>}
                </button>
              </div>
              <div className="p-5">
                {showHtml ? (
                  <textarea
                    ref={htmlTextareaRef}
                    value={htmlContent}
                    onChange={(e) => setHtmlContent(e.target.value)}
                    className={`${inp} font-mono text-xs`}
                    rows={20}
                    placeholder='<div style="white-space:pre-wrap;">{{meta.personalized_body}}</div>'
                    spellCheck={false}
                  />
                ) : (
                  <textarea
                    ref={bodyTextareaRef}
                    value={plainTextContent}
                    onChange={(e) => {
                      richTextTypingRef.current = true;
                      const text = e.target.value;
                      setPlainTextContent(text);
                      setHtmlContent(plainTextToHtml(text));
                      // Reset flag after state update
                      setTimeout(() => { richTextTypingRef.current = false; }, 0);
                    }}
                    className={inp}
                    rows={16}
                    placeholder="Write your email content here… or use {{meta.personalized_body}} for AutoResearch emails"
                  />
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {["{{meta.personalized_body}}", "{{meta.personalized_subject}}", "{{company}}", "{{meta.outreach_angle}}"].map((token) => (
                    <button key={token} onClick={() => {
                      const textarea = showHtml ? htmlTextareaRef.current : bodyTextareaRef.current;
                      if (textarea) {
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        if (showHtml) {
                          const before = htmlContent.slice(0, start);
                          const after = htmlContent.slice(end);
                          setHtmlContent(before + token + after);
                        } else {
                          const before = plainTextContent.slice(0, start);
                          const after = plainTextContent.slice(end);
                          const newText = before + token + after;
                          richTextTypingRef.current = true;
                          setPlainTextContent(newText);
                          setHtmlContent(plainTextToHtml(newText));
                          setTimeout(() => { richTextTypingRef.current = false; }, 0);
                        }
                        // Restore cursor position after the inserted token
                        const newPos = start + token.length;
                        requestAnimationFrame(() => {
                          textarea.focus();
                          textarea.setSelectionRange(newPos, newPos);
                        });
                      } else {
                        // Fallback: append to end
                        if (showHtml) {
                          setHtmlContent(prev => prev + token);
                        } else {
                          const newText = plainTextContent + token;
                          richTextTypingRef.current = true;
                          setPlainTextContent(newText);
                          setHtmlContent(plainTextToHtml(newText));
                          setTimeout(() => { richTextTypingRef.current = false; }, 0);
                        }
                      }
                    }}
                      className="px-2.5 py-1 rounded-lg bg-coral/[0.06] text-coral text-[11px] font-mono font-semibold hover:bg-coral/[0.12] transition-colors">
                      + {token}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Auto-Reply */}
            <div className="bg-white rounded-2xl border border-black/[0.06] p-5 space-y-4">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate">
                  <MessageSquareReply size={13} /> AI Auto-Reply
                </label>
                <button
                  type="button"
                  onClick={() => setAutoReplyEnabled(!autoReplyEnabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${autoReplyEnabled ? "bg-coral" : "bg-black/[0.12]"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${autoReplyEnabled ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
              <p className="text-xs text-slate leading-relaxed">
                When enabled, subscribers who reply to this campaign will automatically receive an AI-generated response crafted by DeepSeek using your instructions below.
              </p>
              {autoReplyEnabled && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-ink mb-2">
                      Reply Instructions
                    </label>
                    <textarea
                      value={autoReplyInstructions}
                      onChange={(e) => setAutoReplyInstructions(e.target.value)}
                      className={`${inp} text-sm`}
                      rows={6}
                      placeholder={"e.g. Reply as a friendly customer success rep named Sarah. Thank them for their interest, answer pricing questions with our standard tiers ($29/mo starter, $99/mo pro), and try to book a demo call. Keep replies under 150 words."}
                    />
                    <p className="text-[11px] text-slate/50 mt-1.5">
                      Be specific about tone, persona, what to include/exclude, and length. The AI will also see the original campaign content and the subscriber's reply for context.
                    </p>
                  </div>

                  {/* Reply Log */}
                  <div className="border-t border-black/[0.06] pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate">
                        Reply History
                      </span>
                      <button
                        onClick={loadAutoReplyLog}
                        disabled={autoReplyLogLoading}
                        className="text-xs text-coral hover:underline font-semibold"
                      >
                        {autoReplyLogLoading ? "Loading…" : "Refresh"}
                      </button>
                    </div>

                    {autoReplyLogLoading && autoReplyLog.length === 0 ? (
                      <div className="flex items-center justify-center py-6 text-slate text-sm">
                        <Loader2 size={14} className="animate-spin mr-2" /> Loading replies…
                      </div>
                    ) : autoReplyLog.length === 0 ? (
                      <p className="text-xs text-slate/60 py-4 text-center">
                        No auto-replies sent yet. Replies will appear here when subscribers respond to this campaign.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {autoReplyLog.map((entry) => (
                          <div key={entry.id} className="rounded-xl border border-black/[0.06] overflow-hidden">
                            <button
                              onClick={() => setExpandedReplyId(expandedReplyId === entry.id ? null : entry.id)}
                              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-black/[0.01] transition-colors"
                            >
                              {entry.status === "sent" ? (
                                <CheckCircle2 size={14} className="text-green-500 shrink-0" />
                              ) : (
                                <AlertCircle size={14} className="text-red-500 shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-ink truncate">
                                  {entry.subscriber_name || entry.subscriber_email || entry.subscriber_id}
                                </p>
                                <p className="text-[11px] text-slate">
                                  {new Date(entry.created_at).toLocaleString()}
                                  {entry.status !== "sent" && (
                                    <span className="ml-2 text-red-500 font-semibold">{entry.status}</span>
                                  )}
                                </p>
                              </div>
                              {expandedReplyId === entry.id ? (
                                <ChevronUp size={14} className="text-slate shrink-0" />
                              ) : (
                                <ChevronDown size={14} className="text-slate shrink-0" />
                              )}
                            </button>

                            {expandedReplyId === entry.id && (
                              <div className="px-4 pb-4 space-y-3 border-t border-black/[0.04]">
                                {(entry.intent || entry.vertical || entry.classification) && (
                                  <div className="mt-3 flex flex-wrap gap-1.5">
                                    {entry.intent && (
                                      <span className="text-[10px] font-semibold text-slate bg-black/[0.05] px-1.5 py-0.5 rounded">
                                        Intent: {entry.intent.replace(/_/g, " ")}
                                      </span>
                                    )}
                                    {entry.vertical && (
                                      <span className="text-[10px] font-semibold text-teal bg-teal-light px-1.5 py-0.5 rounded">
                                        Vertical: {entry.vertical}
                                      </span>
                                    )}
                                    {entry.classification && (
                                      <span className="text-[10px] font-semibold text-ink bg-black/[0.05] px-1.5 py-0.5 rounded">
                                        Class: {entry.classification}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {entry.inbound_text && (
                                  <div className="mt-3">
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-1">Subscriber wrote</p>
                                    <div className="bg-black/[0.02] rounded-lg px-3 py-2.5 text-sm text-ink whitespace-pre-wrap leading-relaxed">
                                      {entry.inbound_text}
                                    </div>
                                  </div>
                                )}
                                {entry.generated_reply && (
                                  <div>
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-1">AI replied</p>
                                    <div className="bg-coral/[0.04] rounded-lg px-3 py-2.5 text-sm text-ink whitespace-pre-wrap leading-relaxed">
                                      {entry.generated_reply}
                                    </div>
                                  </div>
                                )}
                                {entry.error && (
                                  <div>
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-red-500 mb-1">Error</p>
                                    <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{entry.error}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Stats (if sent) */}
            {(campaign.sent_count || 0) > 0 && (
              <div className="bg-white rounded-2xl border border-black/[0.06] p-5">
                <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate mb-3">
                  <Sparkles size={13} /> Performance
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: "Sent", value: campaign.sent_count?.toLocaleString() || "0" },
                    { label: "Opens", value: campaign.open_count?.toLocaleString() || "0" },
                    { label: "Clicks", value: campaign.click_count?.toLocaleString() || "0" },
                    { label: "Open Rate", value: campaign.sent_count ? `${(((campaign.open_count || 0) / campaign.sent_count) * 100).toFixed(1)}%` : "—" },
                  ].map((s) => (
                    <div key={s.label} className="text-center">
                      <p className="text-xs text-slate mb-0.5">{s.label}</p>
                      <p className="text-lg font-bold text-ink">{s.value}</p>
                    </div>
                  ))}
                </div>
                <Link
                  to={`/campaigns/${campaign.id}/send-log`}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-teal hover:underline"
                >
                  View send log →
                </Link>
              </div>
            )}

            {/* Metadata */}
            <div className="text-xs text-slate/50 flex flex-wrap gap-4 px-1">
              <span>Created {new Date(campaign.created_at).toLocaleDateString()}</span>
              {campaign.updated_at && <span>Updated {new Date(campaign.updated_at).toLocaleDateString()}</span>}
              {campaign.sent_at && <span>Sent {new Date(campaign.sent_at).toLocaleString()}</span>}
            </div>
          </>
        )}
      </div>

      {toast && <Toast msg={toast.msg} type={toast.type} />}
    </div>
  );
}
